import {
  hasPermissions,
  getUserPermissions,
  getCurrentAccount,
} from './permissionsHelper';
import { requiresShopifyBilling } from 'v3/helpers/AuthHelper';

import {
  ROLES,
  CONVERSATION_PERMISSIONS,
  CONTACT_PERMISSIONS,
  REPORTS_PERMISSIONS,
  PORTAL_PERMISSIONS,
} from 'dashboard/constants/permissions.js';

export const routeIsAccessibleFor = (route, userPermissions = []) => {
  const { meta: { permissions: routePermissions = [] } = {} } = route;
  return hasPermissions(routePermissions, userPermissions);
};

// [AstraChat] Página inicial de cada MÓDULO do fork, para funções personalizadas
// que só têm módulos (sem conversa/contato/relatório/portal). Sem isso, esse
// usuário caía no fallback `dashboard` (conversas) — que ele não pode acessar — e
// a tela ficava EM BRANCO logo no login. Cada `path` casa uma rota gated pela
// mesma permissão do módulo (senão o guard redirecionaria de novo). `sequences`
// não tem página própria (é aba de Flows) → fica de fora de propósito.
const KANBAN_MODULE_LANDINGS = [
  { permissions: ['kanban_module'], path: 'kanban' },
  { permissions: ['flows_module'], path: 'flows' },
  { permissions: ['projects_module'], path: 'projects' },
  { permissions: ['appointments_module'], path: 'appointments' },
  { permissions: ['disparador_module'], path: 'disparador' },
  { permissions: ['email_module'], path: 'email' },
  { permissions: ['internal_chat_module'], path: 'internal-chats' },
  { permissions: ['crm_reports_module'], path: 'crm-reports' },
  { permissions: ['calls_module'], path: 'calls-report' },
  { permissions: ['synapse_module'], path: 'synapse' },
  // Conexões: o listing de Caixas de Entrada (reconectar número). Ver
  // [[inbox-listing-connection-health]] — settings_inbox_list aceita connections_module.
  { permissions: ['connections_module'], path: 'settings/inboxes/list' },
];

export const defaultRedirectPage = (to, permissions) => {
  const { accountId } = to.params;

  const permissionRoutes = [
    {
      permissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
      path: 'dashboard',
    },
    { permissions: [CONTACT_PERMISSIONS], path: 'contacts' },
    { permissions: [REPORTS_PERMISSIONS], path: 'reports/overview' },
    { permissions: [PORTAL_PERMISSIONS], path: 'portals' },
    // [AstraChat] fallbacks de módulo (avaliados depois das permissões nativas)
    ...KANBAN_MODULE_LANDINGS,
  ];

  const route = permissionRoutes.find(({ permissions: routePermissions }) =>
    hasPermissions(routePermissions, permissions)
  );

  return `accounts/${accountId}/${route ? route.path : 'dashboard'}`;
};

const validateActiveAccountRoutes = (to, user) => {
  // If the current account is active, then check for the route permissions
  const accountDashboardURL = `accounts/${to.params.accountId}/dashboard`;

  // If the user is trying to access suspended route, redirect them to dashboard
  if (to.name === 'account_suspended') {
    return accountDashboardURL;
  }

  const userPermissions = getUserPermissions(user, to.params.accountId);

  const isAccessible = routeIsAccessibleFor(to, userPermissions);
  // If the route is not accessible for the user, return to dashboard screen
  return isAccessible ? null : defaultRedirectPage(to, userPermissions);
};

export const validateLoggedInRoutes = (to, user) => {
  const currentAccount = getCurrentAccount(user, Number(to.params.accountId));
  // If current account is missing, either user does not have
  // access to the account or the account is deleted, return to login screen
  if (!currentAccount) {
    return `app/login`;
  }

  const userPermissions = getUserPermissions(user, to.params.accountId);
  if (requiresShopifyBilling(currentAccount)) {
    if (userPermissions.includes('administrator')) {
      return to.name === 'billing_settings_index'
        ? null
        : `accounts/${to.params.accountId}/settings/billing`;
    }
    return to.name === 'account_suspended'
      ? null
      : `accounts/${to.params.accountId}/suspended`;
  }

  const isCurrentAccountActive = currentAccount.status === 'active';

  if (isCurrentAccountActive) {
    return validateActiveAccountRoutes(to, user);
  }

  // If the current account is not active, only the suspended screen is
  // reachable; administrators can also access billing to restore the account
  const accessibleRoutes = userPermissions.includes('administrator')
    ? ['account_suspended', 'billing_settings_index']
    : ['account_suspended'];

  if (!accessibleRoutes.includes(to.name)) {
    return `accounts/${to.params.accountId}/suspended`;
  }

  // Proceed to the route if none of the above conditions are met
  return null;
};

export const isAConversationRoute = (
  routeName,
  includeBase = false,
  includeExtended = true
) => {
  const baseRoutes = [
    'home',
    'conversation_mentions',
    'conversation_unattended',
    'inbox_dashboard',
    'label_conversations',
    'team_conversations',
    'folder_conversations',
    'conversation_participating',
  ];
  const extendedRoutes = [
    'inbox_conversation',
    'conversation_through_mentions',
    'conversation_through_unattended',
    'conversation_through_inbox',
    'conversations_through_label',
    'conversations_through_team',
    'conversations_through_folders',
    'conversation_through_participating',
  ];

  const routes = [
    ...(includeBase ? baseRoutes : []),
    ...(includeExtended ? extendedRoutes : []),
  ];

  return routes.includes(routeName);
};

export const getConversationDashboardRoute = routeName => {
  switch (routeName) {
    case 'inbox_conversation':
      return 'home';
    case 'conversation_through_mentions':
      return 'conversation_mentions';
    case 'conversation_through_unattended':
      return 'conversation_unattended';
    case 'conversations_through_label':
      return 'label_conversations';
    case 'conversations_through_team':
      return 'team_conversations';
    case 'conversations_through_folders':
      return 'folder_conversations';
    case 'conversation_through_participating':
      return 'conversation_participating';
    case 'conversation_through_inbox':
      return 'inbox_dashboard';
    default:
      return null;
  }
};

export const isAInboxViewRoute = (routeName, includeBase = false) => {
  const baseRoutes = ['inbox_view'];
  const extendedRoutes = ['inbox_view_conversation'];
  const routeNames = includeBase
    ? [...baseRoutes, ...extendedRoutes]
    : extendedRoutes;
  return routeNames.includes(routeName);
};

export const isUpgradePageBypassRoute = routeName =>
  [
    'billing_settings_index',
    'settings_inbox_list',
    'general_settings_index',
    'security_settings_index',
    'agent_list',
  ].includes(routeName);
